import { useEffect, useState } from 'react';
import { createJob } from 'BMapsRedux/jobs/thunks';
import { useDispatch } from 'BMapsRedux';
import { lookupJobDefinition } from 'BMapsSrc/jobman';
import DialogContentSection from 'BMapsUI/common/DialogContentSection';
import ProteinContextProvider from 'BMapsUI/common/ProteinContextProvider';
import { ImportErrors } from 'BMapsUI/dialog/ImportPane/import_components';
import FieldList from './FieldList';
import {
    descriptorsFromJobForm, allDescriptorsFromJobForm, initialStateFromJobForm,
    submitTooltipFromJobForm, deriveFormValues,
} from './formHelpers';

// `jobTypeSelect` is the one widget whose own content is inherently about
// "what alternatives exist alongside me" — `JobForm` fills `jobTypeIds` in
// from its own prop (the tab already supplied it) rather than leaving it
// on the descriptor itself, wherever that descriptor came from.
function withJobTypeIdsInjected(descriptors, jobTypeIds) {
    return descriptors.map((descriptor) => (
        descriptor.widget === 'jobTypeSelect' ? { ...descriptor, jobTypeIds } : descriptor
    ));
}

// `submit` is the one widget whose props are the whole form's business,
// not something it can compute from its own descriptor — `disabled`/
// `tooltip` depend on every descriptor's `required` state, `onClick`
// triggers the whole submit flow. Same injection pattern as `jobTypeIds`
// above.
function withSubmitPropsInjected(descriptors, submitProps) {
    return descriptors.map((descriptor) => (
        descriptor.widget === 'submit' ? { ...descriptor, ...submitProps } : descriptor
    ));
}

// Combines a job's own region with whatever the embedding tab supplied for
// that same named region — tab fields first, then the job's own; the tab's
// title wins if it gave one, otherwise the job's. Either side can be
// entirely absent.
function mergeRegion(jobRegion, tabRegion) {
    if (!jobRegion && !tabRegion) return undefined;
    return {
        title: tabRegion?.title ?? jobRegion?.title,
        fields: { ...(tabRegion?.fields || {}), ...(jobRegion?.fields || {}) },
        subsections: [...(tabRegion?.subsections || []), ...(jobRegion?.subsections || [])],
    };
}

// Finds the one descriptor (if any) using a given widget type across a set
// of regions — how `JobForm` locates "the job-type picker" / "the input
// that names this job" / "the submit button" without assuming any
// particular key. Only the raw regions a tab supplies need searching:
// these widget types only ever come from the tab (a job definition never
// declares them), so there's no need to wait for a specific job to be
// chosen first.
function findDescriptorByWidget(regions, widgetType) {
    return regions
        .flatMap((region) => descriptorsFromJobForm(region?.fields || {}))
        .find((descriptor) => descriptor.widget === widgetType);
}

/**
 * Generic job-selection-and-submission form: render whichever `jobForm`
 * regions the currently-chosen job declares (via `FieldList`), merged with
 * whatever the embedding tab supplies for those same named regions, and
 * submit. A job type contributes its own content — `header`/`body`, each
 * `{ title, fields, subsections? }` — plus `buildRequestPayload`; no
 * rendering, and no other logic, lives on the job definition itself unless
 * a descriptor's behavior can't be expressed in the schema alone.
 *
 * `header`/`body`/`footer` are named regions, not an ordered list or a
 * shared/importable field library, specifically so the embedding *tab* can
 * own exactly what it wants ("this tab's header holds a job-type picker and
 * a job-name field", written right there as ordinary descriptors —
 * see `CompoundGenerationTab.jsx`) without needing to know anything about
 * how many sections a job has or what's in them; the job, in turn, never
 * needs to know it's hosting fields it didn't declare. This is
 * deliberately less flexible than an arbitrary section list would be
 * (exactly three content regions, not N) in exchange for that mutual
 * ignorance: a numeric/positional scheme ("put it in section 2") would
 * leak the job's internal layout to the tab, which is the coupling this
 * is meant to avoid. `footer` is exactly the same shape as `header`/`body`
 * — the submit button is just another `fields` entry (widget `submit`),
 * not special-cased JSX, so a job never declares any of it (a tab's own
 * submit wording is its own business, not any job's).
 *
 * Different tabs can want differently-named/labeled fields playing the
 * same *role* (compound generation's "Job Name" vs. a hypothetical
 * folding tab's "System Name"), so `JobForm` never assumes a fixed key for
 * "the job-type picker" / "the input that names this job" / "the submit
 * button" — it finds them by widget type (`jobTypeSelect` / `jobName` /
 * `submit`) via `findDescriptorByWidget`, wherever the tab put them and
 * whatever it called them.
 *
 * `formState` lives in one place regardless of which job is active.
 * Switching `chosenJobType` drops whatever was only relevant to the
 * previous job (an effect diffs descriptor keys and prunes anything not
 * in the newly-active job's own merged region) while leaving tab-level
 * fields — `chosenJobType`, `jobName`, and any future key two jobs
 * happen to genuinely share — untouched, since every job's own merged
 * region already includes those. What isn't automatic is the
 * newly-relevant job's own defaults: nothing writes them into `formState`
 * on a plain field change, so every read merges them in underneath
 * whatever's already there (`effectiveFormState`) rather than trying to
 * reset `formState` itself — existing values always win, missing ones
 * fall back to the active job's declared defaults.
 *
 * Region numbering ("1. ...", "2. ...") is computed here from render
 * order, not baked into any title string, so header vs. body being present
 * or absent doesn't need every title manually renumbered.
 *
 * What happens after a *successful* submit is the one thing that
 * legitimately varies by where this is embedded (which tab, what it wants
 * to do next), so that's the one thing passed in via `onSubmitSuccess`
 * rather than owned here; resetting the form back to its first job type is
 * generic enough to happen unconditionally either way.
 *
 * The rendered form sits inside its own `ProteinContextProvider`, so widgets can read
 * `ProteinContext` wherever the form is embedded. Its protein selection is independent of any
 * provider above it.
 *
 * @param {{
 *   jobTypeIds: string[],
 *   initData?: object,
 *   onSubmitSuccess?: (results: { jobid?: string }) => void,
 *   header?: { title?: string, fields?: object, subsections?: Array<object> },
 *   body?: { title?: string, fields?: object, subsections?: Array<object> },
 *   footer?: { title?: string, fields?: object },
 * }} props
 */
export default function JobForm({
    jobTypeIds,
    initData,
    onSubmitSuccess,
    header,
    body,
    footer,
}) {
    const dispatch = useDispatch();

    function mergedJobFormFor(jobTypeId) {
        const jobForm = lookupJobDefinition(jobTypeId).jobForm;
        return {
            ...jobForm,
            header: mergeRegion(jobForm.header, header),
            body: mergeRegion(jobForm.body, body),
        };
    }

    const [formState, setFormState] = useState(
        () => initialStateFromJobForm(mergedJobFormFor(jobTypeIds[0]), initData)
    );
    const [errors, setErrors] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const chosenJobTypeDescriptor = findDescriptorByWidget([header, body], 'jobTypeSelect');
    const jobNameDescriptor = findDescriptorByWidget([header, body], 'jobName');
    const submitDescriptor = findDescriptorByWidget([footer], 'submit');

    const chosenJobType = (
        (chosenJobTypeDescriptor && formState[chosenJobTypeDescriptor.key]) || jobTypeIds[0]
    );
    const jobDef = lookupJobDefinition(chosenJobType);
    const jobForm = mergedJobFormFor(chosenJobType);

    // A job's own fields mean nothing once a different job is active — its
    // descriptor keys aren't even read from `formState` while some other
    // job is chosen — so drop them on switch rather than leaving stale,
    // invisible values sitting around. Tab-level fields (`chosenJobType`,
    // `jobName`, and any future one two jobs happen to genuinely share)
    // survive automatically: every job's own merged region already
    // includes them, so they're never outside the surviving key set.
    useEffect(() => {
        const activeKeys = new Set(allDescriptorsFromJobForm(jobForm).map((d) => d.key));
        setFormState((prev) => {
            const next = {};
            let changed = false;
            for (const [key, value] of Object.entries(prev)) {
                if (activeKeys.has(key)) {
                    next[key] = value;
                } else {
                    changed = true;
                }
            }
            return changed ? next : prev;
        });
        // Only `chosenJobType` actually needs to retrigger this — `jobForm`
        // is a fresh object every render regardless, and is read fresh
        // above anyway.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chosenJobType]);

    // Defaults for the active job's own fields (plus whatever the tab
    // merged in), with anything the user's already touched (in
    // `formState`) taking precedence.
    const effectiveFormState = {
        ...initialStateFromJobForm(jobForm, initData),
        ...formState,
    };

    function updateFormState(patch) {
        setFormState((prev) => ({ ...prev, ...patch }));
    }

    async function handleSubmitClick() {
        setIsSubmitting(true);
        const { values: resolvedFormState, errors: resolveErrors } = await deriveFormValues(
            allDescriptorsFromJobForm(jobForm), effectiveFormState
        );
        if (resolveErrors.length > 0) {
            setIsSubmitting(false);
            setErrors(resolveErrors);
            return;
        }

        const { requestData, stash, error } = await jobDef.buildRequestPayload(resolvedFormState);
        if (error) {
            setIsSubmitting(false);
            setErrors(Array.isArray(error) ? error : [error]);
            return;
        }

        const { results, error: submitError } = await dispatch(
            createJob(chosenJobType, {
                ...requestData,
                jobName: jobNameDescriptor ? effectiveFormState[jobNameDescriptor.key] : undefined,
            }, stash)
        );
        setIsSubmitting(false);
        if (results) {
            setFormState(initialStateFromJobForm(mergedJobFormFor(jobTypeIds[0]), initData));
            onSubmitSuccess?.(results);
            return;
        }
        setErrors([`The job submission failed: ${submitError || 'Unspecified error'}`]);
    }

    const { disabled: fieldsDisabled, tooltip: fieldsTooltip } = submitTooltipFromJobForm(
        jobForm, effectiveFormState, submitDescriptor?.tooltip
    );
    const submitProps = {
        disabled: isSubmitting || fieldsDisabled,
        tooltip: isSubmitting ? 'Submitting job...' : fieldsTooltip,
        onClick: handleSubmitClick,
    };

    function renderRegion(region) {
        const descriptors = withSubmitPropsInjected(
            withJobTypeIdsInjected(descriptorsFromJobForm(region?.fields || {}), jobTypeIds),
            submitProps
        );
        return (
            <>
                <FieldList
                    descriptors={descriptors}
                    formState={effectiveFormState}
                    setFormState={updateFormState}
                />
                {(region?.subsections || []).map((sub) => (
                    <DialogContentSection
                        key={sub.title}
                        title={sub.title}
                        collapsible={sub.collapsible}
                        sx={{ marginTop: '1rem' }}
                    >
                        <FieldList
                            descriptors={withJobTypeIdsInjected(
                                descriptorsFromJobForm(sub.fields || {}), jobTypeIds
                            )}
                            formState={effectiveFormState}
                            setFormState={updateFormState}
                        />
                    </DialogContentSection>
                ))}
            </>
        );
    }

    const regions = [
        jobForm.header && {
            key: 'header', title: jobForm.header.title, content: renderRegion(jobForm.header),
        },
        jobForm.body && {
            key: 'body', title: jobForm.body.title, content: renderRegion(jobForm.body),
        },
        footer && { key: 'footer', title: footer.title, content: renderRegion(footer) },
    ].filter(Boolean);

    return (
        <ProteinContextProvider>
            {regions.map(({ key, title, content }, i) => (
                <DialogContentSection key={key} title={`${i + 1}. ${title}`} disablePaddingTop={i > 0}>
                    {content}
                </DialogContentSection>
            ))}
            {!!errors && errors.length > 0 && <ImportErrors errors={errors} />}
        </ProteinContextProvider>
    );
}
